import type { ReceiptView } from "@/services/types";
import type { DateRange } from "@/components/base/DateRangePicker";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { formatINR, formatINRNumber, formatDate, formatDateTime } from "@/utils/format";
import { paymentModeLabel } from "../../components/labels";

interface DayBookPrintProps {
  receipts: ReceiptView[];
  range: DateRange;
  totals: Record<string, number>;
  grandTotal: number;
}

export function DayBookPrint({ receipts, range, totals, grandTotal }: DayBookPrintProps) {
  const active = receipts.filter((r) => !r.reversedAt);
  const label = `${range.start ? formatDate(range.start) : "…"}${range.end ? ` – ${formatDate(range.end)}` : ""}`;
  const modeRows = [
    { key: "cash", label: "Cash" },
    { key: "upi", label: "UPI" },
    { key: "bank_transfer", label: "Bank transfer" },
    { key: "cheque", label: "Cheque" },
  ];

  return (
    <PrintLayout title="Day-end Cash Reconciliation" meta={`Period: ${label}`}>
      <div className="mb-4 grid grid-cols-5 gap-3 print:break-inside-avoid">
        {modeRows.map((m) => (
          <div key={m.key} className="rounded-md border border-background-200 p-2.5 text-center">
            <p className="text-xs text-foreground-500">{m.label}</p>
            <p className="tnum mt-1 text-sm font-semibold">{formatINRNumber(totals[m.key] ?? 0)}</p>
          </div>
        ))}
        <div className="rounded-md border border-foreground-900 p-2.5 text-center">
          <p className="text-xs text-foreground-500">Total</p>
          <p className="tnum mt-1 text-sm font-bold">{formatINR(grandTotal)}</p>
        </div>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead className="print:table-header-group">
          <tr className="border-b-2 border-foreground-900 text-left print:break-inside-avoid">
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Receipt</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Time</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Member</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Group</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Mode</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Amount</th>
          </tr>
        </thead>
        <tbody>
          {active.map((r) => (
            <tr key={r.id} className="border-b border-foreground-200/60 print:break-inside-avoid">
              <td className="tnum whitespace-nowrap px-3 py-2 font-mono font-semibold text-primary-700">{r.receiptNumber}</td>
              <td className="tnum whitespace-nowrap px-3 py-2">{formatDateTime(r.createdAt)}</td>
              <td className="px-3 py-2 font-medium text-foreground-900">{r.memberName}</td>
              <td className="px-3 py-2 text-foreground-600">{r.lines.map((l) => l.groupName).join(" · ")}</td>
              <td className="px-3 py-2 capitalize">{paymentModeLabel(r.mode)}</td>
              <td className="tnum px-3 py-2 text-right font-semibold">{formatINRNumber(r.total)}</td>
            </tr>
          ))}
          {active.length === 0 ? (
            <tr className="print:break-inside-avoid">
              <td colSpan={6} className="py-4 text-center text-foreground-400">No active receipts in this period.</td>
            </tr>
          ) : null}
        </tbody>
        <tfoot className="print:table-footer-group">
          <tr className="border-t-2 border-foreground-900 font-semibold print:break-inside-avoid">
            <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider" colSpan={5}>Total collected</td>
            <td className="tnum px-3 py-2.5 text-right font-bold text-base">{formatINR(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="mt-8 flex justify-between border-t border-foreground-200 pt-2 text-xs text-foreground-500 print:break-inside-avoid">
        <span>Counted by: ______________</span>
        <span>Handed over to: ______________</span>
      </div>
    </PrintLayout>
  );
}